import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Trash2, User, Phone, UserCircle } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TravelChatAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isWaitingForAgent, setIsWaitingForAgent] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  // Lead capture state
  const [showLeadForm, setShowLeadForm] = useState(true);
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadCaptured, setLeadCaptured] = useState(false);

  // Check if lead data exists
  useEffect(() => {
    const storedLead = localStorage.getItem('navmind_lead');
    if (storedLead) {
      const lead = JSON.parse(storedLead);
      setLeadName(lead.name || '');
      setLeadPhone(lead.phone || '');
      setLeadCaptured(true);
      setShowLeadForm(false);
    }
  }, []);

  // Generate or retrieve session ID
  useEffect(() => {
    const storedSessionId = localStorage.getItem('navmind_session_id');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadChatHistory(storedSessionId);
    } else {
      const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      localStorage.setItem('navmind_session_id', newSessionId);
    }
  }, []);

  // Poll for live agent messages (always on when we have a session — chat is direct-to-live-agent)
  useEffect(() => {
    if (sessionId && leadCaptured) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const res = await fetch(`${API_URL}/api/chat/live-messages/${sessionId}`);
          if (res.ok) {
            const data = await res.json();
            if (data.messages && data.messages.length > 0) {
              setMessages(prev => {
                const lastMsgTime = prev.length > 0 ? prev[prev.length - 1].timestamp : null;
                const newMsgs = data.messages.filter(m =>
                  m.role === 'agent' && (!lastMsgTime || new Date(m.timestamp) > new Date(lastMsgTime))
                );
                if (newMsgs.length > 0) {
                  setIsWaitingForAgent(false);
                  return [...prev, ...newMsgs.map(m => ({ ...m, role: 'assistant' }))];
                }
                return prev;
              });
            }
          }
        } catch (err) {
          console.log('Poll error:', err);
        }
      }, 3000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [sessionId, leadCaptured]);

  const loadChatHistory = async (sid) => {
    try {
      const res = await fetch(`${API_URL}/api/chat/live-messages/${sid}`);
      if (res.ok) {
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages.map(m => ({
            ...m,
            role: m.role === 'agent' ? 'assistant' : m.role,
          })));
        }
      }
    } catch (err) {
      console.log('Could not load chat history');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current && !showLeadForm) {
      inputRef.current.focus();
    }
  }, [isOpen, showLeadForm]);

  // Auto-request a live agent whenever a captured lead opens the chat
  const notifyAgent = useCallback(async (name, phone) => {
    if (!sessionId) return;
    setIsWaitingForAgent(true);
    try {
      await fetch(`${API_URL}/api/chat/request-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          customer_name: name,
          customer_phone: phone,
          messages: []
        })
      });
    } catch (err) {
      console.error('Failed to notify agent:', err);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen && leadCaptured && sessionId && messages.length === 0) {
      // First time this session sees the chat: alert agent + seed a welcome system message
      notifyAgent(leadName, leadPhone);
      setMessages([{
        role: 'system',
        content: `Hi ${leadName || 'there'}! Our travel team has been notified. An agent will reply here shortly — feel free to describe your trip while you wait.`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, leadCaptured, sessionId, messages.length, leadName, leadPhone, notifyAgent]);

  // Handle lead form submission
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadName.trim() || !leadPhone.trim()) return;

    const leadData = {
      name: leadName.trim(),
      phone: leadPhone.trim(),
      session_id: sessionId,
      captured_at: new Date().toISOString()
    };

    // Save to localStorage for auto-fill on other pages
    localStorage.setItem('navmind_lead', JSON.stringify(leadData));

    // Save to backend
    try {
      await fetch(`${API_URL}/api/chat/capture-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData)
      });
    } catch (err) {
      console.log('Could not save lead to server');
    }

    setLeadCaptured(true);
    setShowLeadForm(false);
    // Immediately notify a live agent
    notifyAgent(leadData.name, leadData.phone);
    setMessages([{
      role: 'system',
      content: `Hi ${leadData.name}! Our travel team has been notified. An agent will reply here shortly — feel free to describe your trip while you wait.`,
      timestamp: new Date().toISOString()
    }]);
  };

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    const timestamp = new Date().toISOString();
    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp }]);
    setIsLoading(true);

    // Direct-to-live-agent: send every message to the live chat endpoint
    try {
      await fetch(`${API_URL}/api/chat/live-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage,
          sender: 'customer',
          customer_name: leadName
        })
      });
    } catch (err) {
      console.error('Live chat error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, sessionId, leadName]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    if (window.confirm('Clear this conversation? Your details will be kept.')) {
      setMessages([]);
      const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      localStorage.setItem('navmind_session_id', newSessionId);
      setIsWaitingForAgent(true);
    }
  };

  return (
    <>
      {/* Custom slow pulse animation style */}
      <style>{`
        @keyframes slow-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(1.05); }
        }
        .animate-slow-pulse {
          animation: slow-pulse 3s ease-in-out infinite;
        }
      `}</style>
      
      {/* Chat Button - positioned above "Made with Emergent" badge */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-6 z-50 bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 ${isOpen ? 'hidden' : 'flex'} items-center gap-2 group animate-slow-pulse hover:animate-none`}
        data-testid="chat-assistant-button"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap text-sm font-medium">
          Chat with us
        </span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-20 right-6 z-50 w-96 max-w-[calc(100vw-2rem)] h-[32rem] max-h-[calc(100vh-8rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          data-testid="chat-assistant-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <UserCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Live Support</h3>
                <p className="text-xs text-white/80">
                  {isWaitingForAgent ? 'Connecting to agent...' : 'Chat with our travel team'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearChat}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Clear chat"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Lead Capture Form */}
          {showLeadForm && !leadCaptured ? (
            <div className="flex-1 p-6 bg-gradient-to-b from-gray-50 to-white flex flex-col justify-center">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Welcome!</h3>
                <p className="text-sm text-gray-500">Please share your details to start chatting with our travel team</p>
              </div>
              
              <form onSubmit={handleLeadSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      required
                      data-testid="lead-name-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={leadPhone}
                      onChange={(e) => setLeadPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      required
                      data-testid="lead-phone-input"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white py-3 rounded-xl font-medium"
                  data-testid="lead-submit-btn"
                >
                  Start Chatting
                </Button>
              </form>
              
              <p className="text-xs text-gray-400 text-center mt-4">
                Your information is safe with us and will only be used to assist you.
              </p>
            </div>
          ) : (
            <>
              {/* Live Agent Banner */}
              {isWaitingForAgent && (
                <div className="px-4 py-3 bg-blue-50 border-b flex items-center gap-2 text-sm text-blue-700">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Waiting for an agent to connect...</span>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'}`}
                  >
                    {msg.role === 'system' ? (
                      <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm max-w-[90%] text-center">
                        {msg.content}
                      </div>
                    ) : (
                      <div
                        className={`max-w-[85%] p-3 rounded-2xl ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-br-md'
                            : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 bg-white border-t">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message to our agent..."
                    className="flex-1 resize-none border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent max-h-24"
                    rows={1}
                    disabled={isLoading}
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white p-3 rounded-xl"
                    data-testid="chat-send-button"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
                
                <p className="text-xs text-gray-400 mt-2 text-center">
                  You are chatting with our support team &middot;{' '}
                  <a href="/instant-itinerary" className="text-emerald-600 hover:underline">Plan your trip</a>
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default TravelChatAssistant;
